import React, { useState, useEffect, useCallback } from 'react';
import './EndgameTrainer.css';

// Endgame trainer: fetches a random K+material vs K puzzle from the server,
// renders the board interactively, and validates the user's clicked move
// against the engine's suggested best move.
//
// Uses its own click-handling board renderer rather than reusing GameBoard.js
// (1600 lines, tightly coupled to game state) — puzzle mode has different
// semantics (single move to validate, not a full game).

const PIECE_IMAGES = {
  white: { king: '/assets/pieces/white-king.png', knight: '/assets/pieces/white-knight.png', pawn: '/assets/pieces/white-pawn.png', advisor: '/assets/pieces/white-advisor.png', cannon: '/assets/pieces/white-cannon.png', camel: '/assets/pieces/white-camel.png', donkey: '/assets/pieces/white-donkey.png', ram: '/assets/pieces/white-ram.png', spy: '/assets/pieces/white-spy.png' },
  black: { king: '/assets/pieces/black-king.png', knight: '/assets/pieces/black-knight.png', pawn: '/assets/pieces/black-pawn.png', advisor: '/assets/pieces/black-advisor.png', cannon: '/assets/pieces/black-cannon.png', camel: '/assets/pieces/black-camel.png', donkey: '/assets/pieces/black-donkey.png', ram: '/assets/pieces/black-ram.png', spy: '/assets/pieces/black-spy.png' },
};

const CONFIG_OPTIONS = [
  { key: 'random', label: '🎲 Random' },
  { key: 'KA',     label: 'K+Advisor' },
  { key: 'KD',     label: 'K+Donkey' },
  { key: 'KRR',    label: 'K+2 Rams' },
  { key: 'KMM',    label: 'K+2 Camels' },
  { key: 'KR',     label: 'K+Ram (hard)' },
];

const InteractiveBoard = ({ board, attackerColor, selected, revealed, onSquareClick }) => {
  const cells = [];
  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      const piece = board && board[r] && board[r][c];
      const isDark      = (r + c) % 2 === 1;
      const isSelected  = selected && selected.r === r && selected.c === c;
      const isRevealFrom = revealed && revealed.fromRow === r && revealed.fromCol === c;
      const isRevealTo   = revealed && revealed.toRow   === r && revealed.toCol   === c;
      const clickable   = !!onSquareClick;
      const cls = [
        'et-square',
        isDark ? 'et-dark' : 'et-light',
        isSelected  && 'et-selected',
        isRevealFrom && 'et-reveal-from',
        isRevealTo   && 'et-reveal-to',
        clickable    && 'et-clickable',
      ].filter(Boolean).join(' ');
      cells.push(
        <div
          key={`${r}-${c}`}
          className={cls}
          onClick={onSquareClick ? () => onSquareClick(r, c) : undefined}
        >
          {piece && <img src={PIECE_IMAGES[piece.color][piece.type]} alt="" draggable={false} />}
        </div>
      );
    }
  }
  return <div className="et-board">{cells}</div>;
};

const notate = (fr, fc, tr, tc) =>
  `${String.fromCharCode(65 + fc)}${9 - fr}→${String.fromCharCode(65 + tc)}${9 - tr}`;

const EndgameTrainer = ({ onBack }) => {
  const [puzzle, setPuzzle]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [selected, setSelected] = useState(null); // {r, c} for the click-from square
  const [status, setStatus]   = useState('playing'); // 'playing' | 'correct' | 'incorrect' | 'revealed'
  const [config, setConfig]   = useState('random');
  const [stats, setStats]     = useState({ attempted: 0, correct: 0 });

  const loadNext = useCallback(async (cfg = config) => {
    try {
      setLoading(true);
      setError('');
      setSelected(null);
      setStatus('playing');
      const token = localStorage.getItem('authToken');
      const { API_BASE_URL } = await import('../utils/apiBase');
      const q = cfg && cfg !== 'random' ? `?config=${cfg}` : '';
      const res = await fetch(`${API_BASE_URL}/puzzles/generate-endgame${q}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setPuzzle(json);
    } catch (e) {
      setError(e.message || 'failed to load puzzle');
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => { loadNext(config); /* on config change */ }, [config, loadNext]);

  const handleSquareClick = (r, c) => {
    if (!puzzle || status !== 'playing') return;
    const piece = puzzle.board[r][c];
    if (!selected) {
      // First click — must be an attacker's piece.
      if (piece && piece.color === puzzle.attackerColor) {
        setSelected({ r, c });
      }
      return;
    }
    // Second click — validate against solution.
    if (selected.r === r && selected.c === c) {
      setSelected(null); // deselect
      return;
    }
    // Reselect if clicked another own piece.
    if (piece && piece.color === puzzle.attackerColor) {
      setSelected({ r, c });
      return;
    }
    const s = puzzle.solution;
    const isCorrect = selected.r === s.fromRow && selected.c === s.fromCol
                     && r === s.toRow && c === s.toCol;
    setStats((prev) => ({ attempted: prev.attempted + 1, correct: prev.correct + (isCorrect ? 1 : 0) }));
    setStatus(isCorrect ? 'correct' : 'incorrect');
  };

  const reveal = () => setStatus('revealed');

  if (loading) return (
    <div className="et-container">
      <button onClick={onBack} className="et-back">← Back</button>
      <div className="et-loading">Generating puzzle…</div>
    </div>
  );
  if (error) return (
    <div className="et-container">
      <button onClick={onBack} className="et-back">← Back</button>
      <div className="et-error">Error: {error}</div>
      <button onClick={() => loadNext(config)} className="et-primary">Retry</button>
    </div>
  );
  if (!puzzle) return null;

  const revealedMove = (status === 'revealed' || status === 'incorrect')
    ? puzzle.solution
    : (status === 'correct' ? puzzle.solution : null);

  return (
    <div className="et-container">
      <div className="et-header">
        <button onClick={onBack} className="et-back">← Back</button>
        <h1>🏰 Endgame Trainer</h1>
        <div className="et-stats">{stats.correct} / {stats.attempted}</div>
      </div>

      <div className="et-config">
        {CONFIG_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            className={`et-chip ${config === opt.key ? 'et-chip-active' : ''}`}
            onClick={() => setConfig(opt.key)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="et-body">
        <InteractiveBoard
          board={puzzle.board}
          attackerColor={puzzle.attackerColor}
          selected={selected}
          revealed={revealedMove}
          onSquareClick={status === 'playing' ? handleSquareClick : null}
        />

        <div className="et-side">
          <div className="et-position-info">
            <div className="et-label">{puzzle.label}</div>
            <div className="et-difficulty">{puzzle.difficulty}</div>
            <div className="et-to-move">{puzzle.attackerColor} to move — find the best move</div>
          </div>

          {status === 'playing' && (
            <div className="et-hint">
              {selected
                ? `Piece at ${String.fromCharCode(65 + selected.c)}${9 - selected.r} selected. Click a destination.`
                : 'Click one of your pieces.'}
            </div>
          )}

          {status === 'correct' && (
            <div className="et-feedback et-correct">
              ✅ Correct! Engine agrees: {notate(puzzle.solution.fromRow, puzzle.solution.fromCol, puzzle.solution.toRow, puzzle.solution.toCol)}
            </div>
          )}

          {status === 'incorrect' && (
            <div className="et-feedback et-incorrect">
              ❌ Not the top choice. Engine suggests: {notate(puzzle.solution.fromRow, puzzle.solution.fromCol, puzzle.solution.toRow, puzzle.solution.toCol)}
            </div>
          )}

          {status === 'revealed' && (
            <div className="et-feedback et-revealed">
              💡 Engine plays: {notate(puzzle.solution.fromRow, puzzle.solution.fromCol, puzzle.solution.toRow, puzzle.solution.toCol)}
            </div>
          )}

          <div className="et-actions">
            {status === 'playing' && (
              <button className="et-secondary" onClick={reveal}>Show solution</button>
            )}
            <button className="et-primary" onClick={() => loadNext(config)}>
              {status === 'playing' ? 'Skip →' : 'Next puzzle →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EndgameTrainer;
